import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { quickbooksService } from '../services/quickbooks.service'; // Corrected import path
import { API_BASE_URL } from '../config'; // Import base URL for auth redirect

// Define the expected shape of the token status response
interface TokenStatus {
    tokenExists: boolean;
    isValid: boolean;
    isExpired: boolean;
    expiresAt: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    realmId: string | null;
}

// Helper function to format remaining time
const formatRemainingTime = (expiresAt: string | null): string => {
    if (!expiresAt) return 'N/A';
    
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffSeconds = Math.round((expiryDate.getTime() - now.getTime()) / 1000);

    if (diffSeconds <= 0) return 'Expired';

    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;

    if (minutes > 0) {
        return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
};


const QuickbooksTokenManagement: React.FC = () => {
    const [tokenStatus, setTokenStatus] = useState<TokenStatus | null>(null); 
    const [remainingTime, setRemainingTime] = useState<string>('N/A'); // State for countdown
    const [loading, setLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [accessToken, setAccessToken] = useState('');
    const [refreshToken, setRefreshToken] = useState('');
    const [expiresIn, setExpiresIn] = useState<number>(3600);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        fetchTokenStatus();
        
        // If token exists and is valid, fetch expense accounts
        if (tokenStatus?.tokenExists && tokenStatus?.isValid && !tokenStatus?.isExpired) {
            fetchExpenseAccounts();
        }
    }, [tokenStatus?.tokenExists, tokenStatus?.isValid, tokenStatus?.isExpired]);
    
    // Function to fetch expense accounts
    const fetchExpenseAccounts = async () => {
        if (!tokenStatus?.tokenExists || !tokenStatus?.isValid || tokenStatus?.isExpired) {
            return;
        }
        
        setAccountsLoading(true);
        try {
            const response = await quickbooksService.getExpenseAccounts();
            setExpenseAccounts(response.accounts);
            setSelectedAccountId(response.selectedAccountId);
        } catch (error) {
            console.error('Error fetching expense accounts:', error);
        } finally {
            setAccountsLoading(false);
        }
    };
    
    // Function to save selected expense account
    const handleSaveExpenseAccount = async () => {
        if (!selectedAccountId) {
            alert('Please select an expense account');
            return;
        }
        
        setAccountSaveLoading(true);
        try {
            const result = await quickbooksService.setExpenseAccount(selectedAccountId);
            alert(`Default expense account set to: ${result.accountName}`);
            
            // Refresh the expense accounts list
            await fetchExpenseAccounts();
        } catch (error: any) {
            console.error('Error setting expense account:', error);
            
            // Extract and display the error message
            let errorMessage = 'Failed to set expense account. Please try again.';
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            alert(errorMessage);
        } finally {
            setAccountSaveLoading(false);
        }
    };

    // Effect to update countdown timer
    useEffect(() => {
        if (tokenStatus?.expiresAt) {
            // Update immediately
            setRemainingTime(formatRemainingTime(tokenStatus.expiresAt));

            // Set interval to update every minute
            const intervalId = setInterval(() => {
                const timeLeft = formatRemainingTime(tokenStatus.expiresAt);
                setRemainingTime(timeLeft);
                if (timeLeft === 'Expired') {
                    clearInterval(intervalId); // Stop timer if expired
                    fetchTokenStatus(); // Re-fetch status after expiration
                }
            }, 60000); // Update every 60 seconds

            // Cleanup function
            return () => clearInterval(intervalId);
        } else {
            setRemainingTime('N/A'); // Reset if no expiry date
        }
    }, [tokenStatus?.expiresAt]); // Rerun effect if expiresAt changes

    const fetchTokenStatus = async () => {
        try {
            // Fetch status from API
            const apiStatus = await quickbooksService.getTokenStatus(); // Use imported service instance
            
            // Construct the full TokenStatus object defensively
            const fullStatus: TokenStatus = {
                tokenExists: apiStatus?.tokenExists ?? false,
                isValid: apiStatus?.isValid ?? false,
                isExpired: apiStatus?.isExpired ?? true, // Default to expired if unknown
                expiresAt: apiStatus?.expiresAt ?? null,
                accessToken: apiStatus?.accessToken ?? null,
                refreshToken: apiStatus?.refreshToken ?? null,
                realmId: apiStatus?.realmId ?? null
            };
            setTokenStatus(fullStatus);
        } catch (error) {
            console.error('Error fetching token status:', error);
            setTokenStatus(null); // Reset status on error
            // Consider displaying an error message to the user
        }
    };

    const [refreshError, setRefreshError] = useState<string | null>(null);
    const [clearLoading, setClearLoading] = useState(false);

    const handleRefreshToken = async () => {
        setLoading(true);
        setRefreshError(null);
        try {
            // Assuming refreshQuickbooksToken is also part of the service, or needs similar update
            // For now, let's assume it's handled elsewhere or needs separate fixing if used.
            // If refreshQuickbooksToken was also from '../api', it needs updating too.
            // Let's focus on getTokenStatus first. We might need another step if refresh fails.
            await quickbooksService.refreshToken(); // Use the service method
            await fetchTokenStatus(); // Refresh the token status after refreshing
        } catch (error: any) { // Type assertion to 'any' to handle axios error
            console.error('Error refreshing token:', error);
            
            // Extract and display the error message
            let errorMessage = 'Failed to refresh token. Please try reconnecting to QuickBooks.';
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            setRefreshError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClearToken = async () => {
        if (!window.confirm('Are you sure you want to clear the QuickBooks token? This will disconnect your account from QuickBooks.')) {
            return;
        }
        
        setClearLoading(true);
        try {
            // Assuming clearQuickbooksToken is also part of the service or needs update
            await quickbooksService.clearToken(); // Use the service method
            await fetchTokenStatus(); // Refresh the token status after clearing
        } catch (error: any) {
            console.error('Error clearing token:', error);
            
            // Extract and display the error message
            let errorMessage = 'Failed to clear token.';
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            alert(errorMessage);
        } finally {
            setClearLoading(false);
        }
    };

    const handleCreateToken = async () => {
        if (!accessToken || !refreshToken || !expiresIn) {
            alert('Please fill in all fields');
            return;
        }

        setCreateLoading(true);
        try {
            // Assuming createQuickbooksToken is also part of the service or needs update
            await quickbooksService.createToken({ // Use the service method
                accessToken,
                refreshToken,
                expiresIn
            });
            await fetchTokenStatus(); // Refresh the token status after creating
            setAccessToken('');
            setRefreshToken('');
            setExpiresIn(3600);
            setShowCreateForm(false);
        } catch (error) {
            console.error('Error creating token:', error);
            // Handle error appropriately (e.g., display an error message)
        } finally {
            setCreateLoading(false);
        }
    };

    const handleConnectToQuickbooks = () => {
        // Redirect the browser to the backend auth endpoint
        window.location.href = `${API_BASE_URL}/quickbooks/auth`;
    };

    // Expense account states
    const [expenseAccounts, setExpenseAccounts] = useState<Array<{id: string; name: string; type: string; isSelected: boolean}>>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountSaveLoading, setAccountSaveLoading] = useState(false);
    
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncAllLoading, setSyncAllLoading] = useState(false);
    const [productId, setProductId] = useState<number | null>(null);

    const handleSyncSingleProduct = async () => {
        if (!productId) {
            alert('Please enter a product ID');
            return;
        }
        
        setSyncLoading(true);
        try {
            // Use the syncSingleProduct method from quickbooksService
            const result = await quickbooksService.syncSingleProduct(productId);
            
            // Check if this is a server error response
            if (result.isServerError) {
                // Display a more user-friendly error message for server errors
                const errorMessage = `
QuickBooks API Server Error

${result.message}

This is likely a temporary issue with the QuickBooks API and not a problem with your data.
Please try again later or contact support if the issue persists.

Details: ${result.details || 'No additional details available'}
                `.trim();
                
                alert(errorMessage);
            } else if (result.success === false) {
                // Handle other error responses
                alert(result.message);
            } else {
                // Handle success
                alert(result.message);
            }
        } catch (error: any) {
            console.error('Error syncing product with QuickBooks:', error);
            // Extract and display the error message for unexpected errors
            let errorMessage = 'Failed to sync product with QuickBooks. Please try again.';
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            alert(errorMessage);
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncAllProducts = async () => {
        if (!window.confirm('Are you sure you want to sync ALL products with QuickBooks? This may take a while.')) {
            return;
        }
        
        setSyncAllLoading(true);
        try {
            // Use the syncAllProducts method from quickbooksService
            const result = await quickbooksService.syncAllProducts();
            alert(result.message); // Show success message
        } catch (error: any) {
            console.error('Error syncing all products with QuickBooks:', error);
            // Extract and display the error message
            let errorMessage = 'Failed to sync all products with QuickBooks. Please try again.';
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            alert(errorMessage);
        } finally {
            setSyncAllLoading(false);
        }
    };

    return (
        <div>
            <h1>QuickBooks Token Management</h1>
            {tokenStatus ? (
                <div>
                    <p>Token Exists: {tokenStatus.tokenExists ? 'Yes' : 'No'}</p>
                    {tokenStatus.tokenExists && (
                        <>
                            <p>Token Valid: {tokenStatus.isValid ? 'Yes' : 'No'}</p>
                            <p>Token Expired: {tokenStatus.isExpired ? 'Yes' : 'No'}</p>
                            
                            {tokenStatus.expiresAt && (
                                <>
                                    <p>
                                        Token Expires At: {new Date(tokenStatus.expiresAt).toLocaleString()} 
                                    </p>
                                    <p>
                                        Time Remaining: {remainingTime} 
                                    </p>
                                </>
                            )}
                            
                            {/* Display unencrypted tokens */}
                            {tokenStatus.tokenExists && (
                                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                                    <h3>Unencrypted Tokens</h3>
                                    <div style={{ marginBottom: '1rem' }}>
                                        <h4>Access Token</h4>
                                        <div style={{ 
                                            padding: '0.5rem', 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #ced4da', 
                                            borderRadius: '4px',
                                            overflowX: 'auto',
                                            wordBreak: 'break-all',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace'
                                        }}>
                                            {tokenStatus.accessToken}
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: '1rem' }}>
                                        <h4>Refresh Token</h4>
                                        <div style={{ 
                                            padding: '0.5rem', 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #ced4da', 
                                            borderRadius: '4px',
                                            overflowX: 'auto',
                                            wordBreak: 'break-all',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace'
                                        }}>
                                            {tokenStatus.refreshToken}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h4>Realm ID</h4>
                                        <div style={{ 
                                            padding: '0.5rem', 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #ced4da', 
                                            borderRadius: '4px',
                                            overflowX: 'auto',
                                            wordBreak: 'break-all',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace'
                                        }}>
                                            {tokenStatus.realmId}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div className="p-d-flex p-jc-between" style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                        <Button 
                            label={tokenStatus.tokenExists ? "Reconnect to QuickBooks" : "Connect to QuickBooks"} 
                            icon="pi pi-link" 
                            onClick={handleConnectToQuickbooks} 
                            className="p-button-primary"
                        />
                        <Button 
                            label="Refresh Token" 
                            icon="pi pi-refresh" 
                            onClick={handleRefreshToken} 
                            loading={loading} 
                            disabled={!tokenStatus.tokenExists}
                        />
                        <Button 
                            label="Clear Token" 
                            icon="pi pi-trash" 
                            onClick={handleClearToken} 
                            loading={clearLoading} 
                            disabled={!tokenStatus.tokenExists}
                            className="p-button-danger"
                        />
                        <Button 
                            label={showCreateForm ? "Hide Form" : "Manual Token Entry"} 
                            icon={showCreateForm ? "pi pi-times" : "pi pi-plus"} 
                            onClick={() => setShowCreateForm(!showCreateForm)} 
                            className={showCreateForm ? "p-button-secondary" : "p-button-success"}
                        />
                    </div>
                    
                    {refreshError && (
                        <div style={{ 
                            marginTop: '1rem', 
                            padding: '1rem', 
                            backgroundColor: '#FFEBEE', 
                            color: '#D32F2F', 
                            borderRadius: '4px',
                            border: '1px solid #FFCDD2'
                        }}>
                            <h3 style={{ margin: '0 0 0.5rem 0' }}>Error Refreshing Token</h3>
                            <p style={{ margin: '0' }}>{refreshError}</p>
                            <p style={{ marginTop: '0.5rem' }}>
                                Try reconnecting to QuickBooks by clicking the "Connect to QuickBooks" button above.
                            </p>
                        </div>
                    )}
                    
                    {/* Expense Account Selection */}
                    {tokenStatus.tokenExists && tokenStatus.isValid && !tokenStatus.isExpired && (
                        <div style={{ marginTop: '2rem' }}>
                            <h2>QuickBooks Expense Account</h2>
                            <p>Select the default expense account to use when syncing products to QuickBooks.</p>
                            
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                                <Dropdown
                                    value={selectedAccountId}
                                    options={expenseAccounts.map(account => ({
                                        label: `${account.name} (${account.type})`,
                                        value: account.id
                                    }))}
                                    onChange={(e) => setSelectedAccountId(e.value)}
                                    placeholder="Select an Expense Account"
                                    style={{ width: '300px' }}
                                    disabled={accountsLoading}
                                />
                                <Button
                                    label="Save Default Account"
                                    icon="pi pi-save"
                                    onClick={handleSaveExpenseAccount}
                                    loading={accountSaveLoading}
                                    disabled={!selectedAccountId || accountsLoading}
                                    className="p-button-success"
                                />
                                <Button
                                    label="Refresh Accounts"
                                    icon="pi pi-refresh"
                                    onClick={fetchExpenseAccounts}
                                    loading={accountsLoading}
                                    className="p-button-outlined"
                                />
                            </div>
                            <small>This account will be used for all products synced to QuickBooks.</small>
                        </div>
                    )}
                    
                    <div style={{ marginTop: '2rem' }}>
                        <h2>QuickBooks Sync</h2>
                        <p>Sync your products with QuickBooks to ensure they are up-to-date.</p>
                        
                        <div style={{ marginBottom: '1rem' }}>
                            <h3>Sync Single Product</h3>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <InputNumber 
                                    placeholder="Enter Product ID" 
                                    value={productId} 
                                    onValueChange={(e) => setProductId(e.value || null)}
                                    min={1}
                                    style={{ width: '200px' }}
                                />
                                <Button 
                                    label="Sync Single Product" 
                                    icon="pi pi-sync" 
                                    onClick={handleSyncSingleProduct} 
                                    loading={syncLoading} 
                                    disabled={!tokenStatus.tokenExists || !tokenStatus.isValid || tokenStatus.isExpired || !productId}
                                    className="p-button-info"
                                />
                            </div>
                            <small>Enter a product ID to sync only that product with QuickBooks.</small>
                        </div>
                        
                        <div>
                            <h3>Sync All Products</h3>
                            <Button 
                                label="Sync All Products with QuickBooks" 
                                icon="pi pi-sync" 
                                onClick={handleSyncAllProducts} 
                                loading={syncAllLoading} 
                                disabled={!tokenStatus.tokenExists || !tokenStatus.isValid || tokenStatus.isExpired}
                                className="p-button-info"
                            />
                            <div style={{ marginTop: '0.5rem' }}>
                                <small>Warning: This will sync all products and may take a while.</small>
                            </div>
                        </div>
                    </div>
                    
                    {showCreateForm && (
                        <div className="p-fluid" style={{ marginTop: '1rem' }}>
                            <h2>Manual Token Entry</h2>
                            <p>Use this form only if you have manually obtained QuickBooks tokens. For most users, the "Connect to QuickBooks" button is recommended.</p>
                            <div className="p-field" style={{ marginBottom: '1rem' }}>
                                <label htmlFor="accessToken">Access Token</label>
                                <InputText 
                                    id="accessToken" 
                                    value={accessToken} 
                                    onChange={(e) => setAccessToken(e.target.value)} 
                                    placeholder="Enter access token"
                                />
                            </div>
                            <div className="p-field" style={{ marginBottom: '1rem' }}>
                                <label htmlFor="refreshToken">Refresh Token</label>
                                <InputText 
                                    id="refreshToken" 
                                    value={refreshToken} 
                                    onChange={(e) => setRefreshToken(e.target.value)} 
                                    placeholder="Enter refresh token"
                                />
                            </div>
                            <div className="p-field" style={{ marginBottom: '1rem' }}>
                                <label htmlFor="expiresIn">Expires In (seconds)</label>
                                <InputNumber 
                                    id="expiresIn" 
                                    value={expiresIn} 
                                    onValueChange={(e) => setExpiresIn(e.value || 3600)} 
                                    min={1}
                                />
                            </div>
                            <Button 
                                label="Create Token" 
                                icon="pi pi-check" 
                                onClick={handleCreateToken} 
                                loading={createLoading}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <p>Loading token status...</p>
            )}
        </div>
    );
};

export default QuickbooksTokenManagement;
